import React from 'react';

/**
 * Диагностика движка OutfitScore (outfit-score-engine, FR-3): код, серьёзность,
 * вклад в балл и русский детерминированный текст.
 */
export interface OutfitScoreDiagnostic {
    code: string;
    severity: 'REINFORCING' | 'WARNING' | 'MAJOR' | 'INVALID' | string;
    pair?: string | null;
    component?: string | null;
    actual?: number | null;
    threshold?: number | null;
    contribution?: number | null;
    message: string;
}

/** Тон бейджа по шкале движка: 80+ хорошо, 60–79 пограничный, ниже — конфликт. */
export function outfitScoreTone(score: number): {badge: string; text: string} {
    if (score >= 80) {
        return {
            badge: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400',
            text: 'text-green-500',
        };
    }
    if (score >= 60) {
        return {
            badge: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
            text: 'text-amber-400',
        };
    }
    return {
        badge: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
        text: 'text-red-500',
    };
}

interface Props {
    /** OutfitScore 0–100; null/undefined — образ не оценён (unscored). */
    score?: number | null;
    /** Русская метка шкалы движка (scoreLabel). */
    label?: string | null;
    /** Идёт пересчёт (живая оценка в конструкторе). */
    loading?: boolean;
    /** Текст ошибки расчёта; показывается вместо бейджа. */
    error?: string | null;
    className?: string;
}

/**
 * Бейдж единого OutfitScore (outfit-score-engine): балл 0–100 с меткой и тоном по шкале
 * движка. Состояния loading/unscored/error — по styleguide (нейтральные, без алармизма).
 */
const OutfitScoreBadge: React.FC<Props> = ({score, label, loading, error, className = ''}) => {
    if (loading) {
        return (
            <span data-testid="outfit-score-loading"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-ink-800 text-gray-500 dark:text-gray-400 animate-pulse ${className}`}>
                Оцениваем…
            </span>
        );
    }
    if (error) {
        return (
            <span data-testid="outfit-score-error"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-ink-800 text-gray-500 dark:text-gray-400 ${className}`}>
                {error}
            </span>
        );
    }
    if (score == null) {
        return (
            <span data-testid="outfit-score-unscored"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-ink-800 text-gray-500 dark:text-gray-400 ${className}`}>
                {label || 'Недостаточно данных'}
            </span>
        );
    }
    const rounded = Math.round(score);
    const tone = outfitScoreTone(rounded);
    return (
        <span data-testid="outfit-score-badge"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${tone.badge} ${className}`}>
            {/* Шкала в подписи: «86» без «/100» не читалось (QA-15). */}
            <span className="font-bold text-sm leading-none" data-testid="outfit-score-value">
                {rounded}<span className="font-normal opacity-70">/100</span>
            </span>
            {label && <span>{label}</span>}
        </span>
    );
};

export default OutfitScoreBadge;
